import { getCustomChartContext } from '@thoughtspot/ts-chart-app';
import { GraphQLError } from 'graphql';
import _ from 'lodash';
import { useRef } from 'react';
import {
    ErrorPolicy,
    useApolloClient,
    useMutation,
} from '../../utils/error-handling/apollo-hooks-wrappers/custom-apollo-hooks';
import {
    getClientStateRequest,
    getUpdatedCustomVisualProp,
} from '../answer/answer.util';
import {
    getAllClientStateMutation,
    getCachedAnswerVisualization,
    isUserAction,
    isVizPropChanged,
    updateVizPropWithUserActionForRefAnswer,
} from '../answer/answer-service';
import {
    useBachLazyPinboardQuery,
    useBachLazyQuery,
    useBachMutation,
} from '../bach/bach';
import { useGatedBachMutation } from '../bach/bach-gated';
import {
    AddVizToPinboardDocument,
    AddVizToPinboardMutation,
    AddVizToPinboardMutationVariables,
    AddVizToPinboardWithSessionDocument,
    AddVizToPinboardWithSessionMutation,
    AddVizToPinboardWithSessionMutationVariables,
    CreatePinboardByNameDocument,
    CreatePinboardByNameMutation,
    CreatePinboardByNameMutationVariables,
    CreatePinboardTabDocument,
    CreatePinboardTabMutation,
    CreatePinboardTabMutationVariables,
    GetPinboardListDocument,
    GetPinboardListQuery,
    GetPinboardListQueryVariables,
    GetPinboardTabListDocument,
    GetPinboardTabListQuery,
    GetPinboardTabListQueryVariables,
    useGetDetailedPinboardQuery as _useGetDetailedPinboardQuery,
    useGetPinboardInfoQuery as _useGetPinboardInfoQuery,
    useGetPinboardListQuery as _useGetPinboardListQuery,
    useGetPinboardsListQuery as _useGetPinboardsListQuery,
    useGetRecentlyPinnedPinboardQuery as _useGetRecentlyPinnedPinboardQuery,
} from '../generated/graphql-types';

export const useGetPinboardListLazyQuery = (answerSessionId: string) => {
    const [
        _getUpdatedPinboardList,
        { data, error, loading },
    ] = useBachLazyQuery<GetPinboardListQuery, GetPinboardListQueryVariables>(
        answerSessionId,
        GetPinboardListDocument,
        {
            fetchPolicy: 'no-cache',
        },
    );

    const getUpdatedPinboardList = (
        filterPinboardsPattern = '',
        fetchPermissions = true,
    ) =>
        _getUpdatedPinboardList({
            variables: {
                params: {
                    autoCreated: false,
                    showHidden: false,
                    sortAscending: false,
                    pattern: filterPinboardsPattern,
                    fetchPermissions,
                },
            },
        });

    return {
        getUpdatedPinboardList,
        error,
        loading,
        data: data?.getPinboardList,
    };
};

export const useGetPinboardListWithPinboardSessionIdLazyQuery = (
    pinboardSessionId: string,
) => {
    const [
        _getUpdatedPinboardList,
        { data, error, loading },
    ] = useBachLazyPinboardQuery<
        GetPinboardListQuery,
        GetPinboardListQueryVariables
    >(pinboardSessionId, GetPinboardListDocument, {
        fetchPolicy: 'no-cache',
    });

    const getUpdatedPinboardList = (
        filterPinboardsPattern = '',
        fetchPermissions = true,
    ) =>
        _getUpdatedPinboardList({
            variables: {
                params: {
                    autoCreated: false,
                    showHidden: false,
                    sortAscending: false,
                    pattern: filterPinboardsPattern,
                    fetchPermissions,
                },
            },
        });

    return {
        getUpdatedPinboardList,
        error,
        loading,
        data: data?.getPinboardList,
    };
};

export const useGetPinboardListQuery = (
    answerSessionId: string,
    batchSize: number,
    fetchPermissions = true,
) => {
    const { data, error, loading } = _useGetPinboardListQuery({
        fetchPolicy: 'network-only',
        variables: {
            params: {
                batchSize,
                autoCreated: false,
                showHidden: false,
                sortAscending: false,
                fetchPermissions,
            },
        },
    });
    return {
        data: data?.getPinboardList?.objects || [],
    };
};

export const useCreatePinboardByNameMutation = () => {
    const [_createPinboard, { error, loading }] = useMutation<
        CreatePinboardByNameMutation,
        CreatePinboardByNameMutationVariables
    >(CreatePinboardByNameDocument);
    return {
        _createPinboard,
        error,
        loading,
    };
};

export const useGetPinboardInfoQuery = (id: string) => {
    const { data, error, loading } = _useGetPinboardInfoQuery({
        fetchPolicy: 'network-only',
        variables: {
            id,
        },
    });
    return {
        data: data?.getPinboardInfo || {},
        error,
        loading,
    };
};

export const useGetDetailedPinboardQuery = (id: string) => {
    const { data, error, loading } = _useGetDetailedPinboardQuery({
        fetchPolicy: 'network-only',
        variables: {
            id,
        },
    });
    return {
        data: data?.getDetailedPinboardInfo || {},
        error,
        loading,
    };
};

export const useGetPinboardTabList = (answerSessionId: string) => {
    const [_getTabListInfo, { data, error, loading }] = useBachLazyQuery<
        GetPinboardTabListQuery,
        GetPinboardTabListQueryVariables
    >(answerSessionId, GetPinboardTabListDocument, {
        fetchPolicy: 'no-cache',
    });

    const getPinboardTabInfo = (id: string) =>
        _getTabListInfo({
            variables: {
                id,
            },
        });

    return {
        getPinboardTabInfo,
        error,
        loading,
        data: data?.getPinboardTabList,
    };
};

export const useAddVizToPinboardMutation = (
    answerSessionId: string,
    handledErrorCodes: string[] = [],
) => {
    const client = useApolloClient();
    const { cache } = client;
    const cachedAnswer = getCachedAnswerVisualization(cache, answerSessionId);
    const visualizations = cachedAnswer?.answer?.visualizations || [];

    let document = AddVizToPinboardWithSessionDocument;

    // this condition we have added for preventing unnecessory viz prop mutation call
    // Now vizProp mutation will get called only when viz prop changedit
    const isVizPropChangedForQuery = isVizPropChanged(cache, answerSessionId);

    // the system update of viz prop should not update gen number and hence
    // we should Identify that if vizprop was updated by system or user
    const isUserPerformedAction = isUserAction(cache, answerSessionId);

    if (isVizPropChangedForQuery) {
        const combineQueryDoc = (() =>
            getAllClientStateMutation(
                'addVizToPinboardrAndUpdateVizProps',
                visualizations,
            ).add(AddVizToPinboardDocument))();
        document = combineQueryDoc.document;
    }

    // useGatedBachMutation is used here instead of useBachMutation to defer
    // the pin operation until isHydrated is true. If the viz session is still
    // being refreshed from cache, firing this mutation immediately would send
    // stale session IDs to the server. The gate holds the call and replays it
    // once the fresh session data is available.
    const [_addVizToPinboard, { data, error, loading }] = useGatedBachMutation<
        AddVizToPinboardMutation | AddVizToPinboardWithSessionMutation,
        | AddVizToPinboardMutationVariables
        | AddVizToPinboardWithSessionMutationVariables
    >(
        answerSessionId,
        document,
        {
            setNextGenNoShouldReplaceCurrent: !isUserPerformedAction,
        },
        handledErrorCodes,
    );
    const {
        persistedVisualPropKeys,
        globalPersistedKeyVisualProp,
    } = getCustomChartContext();

    // The ref ensures the async addVizToPinboard callback always reads the
    // latest answerSessionId. During cache hydration the session ID can change
    // after the callback is created, so a direct closure capture would be stale.
    const answerSessionIdRef = useRef(answerSessionId);
    answerSessionIdRef.current = answerSessionId;

    const addVizToPinboard = async ({
        vizId,
        newVizName,
        newVizDescription,
        newDynamicName,
        newDynamicDescription,
        pinboardId,
        tabId,
        newPinboardName,
        newTabName,
        pinFromStore,
    }: {
        vizId: string;
        newVizName: string;
        newVizDescription?: string;
        newDynamicName?: string;
        newDynamicDescription?: string;
        pinboardId?: string;
        tabId?: string;
        newPinboardName?: string;
        newTabName?: string;
        pinFromStore?: boolean;
    }): Promise<{
        pinboardId: string;
        tabId: string;
        vizId: string;
        errors: readonly GraphQLError[];
    }> => {
        if (_.isNil(pinboardId) && _.isNil(newPinboardName)) {
            return Promise.reject(
                new Error(
                    'Both pinboardId and newPinboardName cannot be empty',
                ),
            );
        }
        const currentSessionId = answerSessionIdRef.current;
        let updatedVisualProps: unknown;
        if (currentSessionId && currentSessionId !== '') {
            updatedVisualProps = getUpdatedCustomVisualProp(
                client,
                persistedVisualPropKeys,
                globalPersistedKeyVisualProp,
                currentSessionId,
            );
        }
        const clientStateRequests = getClientStateRequest(
            visualizations,
            currentSessionId,
            updatedVisualProps,
        );
        let variables = {
            vizId,
            newVizName,
            newVizDescription,
            newVizDynamicName: newDynamicName,
            newVizDynamicDescription: newDynamicDescription,
            pinboardId,
            newPinboardName,
            tabId,
            newTabName,
            pinFromStore,
        };
        if (isVizPropChangedForQuery) {
            variables = {
                ...variables,
                sessionId: currentSessionId,
                ...clientStateRequests,
            };
        }
        const result = await _addVizToPinboard({
            variables,
        });
        updateVizPropWithUserActionForRefAnswer(
            client,
            answerSessionIdRef.current,
            false,
            true,
        ); // passing user action as true as save is triggered by user
        return {
            pinboardId: result?.data?.Answer__addVizToPinboard?.pinboardId,
            tabId: result?.data?.Answer__addVizToPinboard?.tabId,
            vizId: result?.data?.Answer__addVizToPinboard?.vizId,
            errors: result?.errors as GraphQLError[] | undefined,
        };
    };

    return { addVizToPinboard, data, error, loading };
};

export const useGetPinboardsListQuery = (params: any) => {
    const { data, error, loading } = _useGetPinboardsListQuery({
        fetchPolicy: 'network-only',
        variables: { params },
    });

    return {
        error,
        loading,
        data: data?.getPinboardList,
    };
};

export const useGetRecentlyUsedPinboard = (
    errorPolicy: ErrorPolicy = 'none',
) => {
    const { data, error, loading } = _useGetRecentlyPinnedPinboardQuery({
        fetchPolicy: 'network-only',
        errorPolicy,
    });

    return {
        error,
        loading,
        data: data?.getRecentlyPinnedPinboard,
    };
};

export const createPinboardTab = () => {
    const [_createPinboardTab, { data, error, loading }] = useMutation<
        CreatePinboardTabMutation,
        CreatePinboardTabMutationVariables
    >(CreatePinboardTabDocument, { onError: _.noop });
    const createTab = async (pinboardId: string, name: string) => {
        return _createPinboardTab({
            variables: {
                id: pinboardId,
                name,
            },
        });
    };
    return {
        createTab,
        error,
        loading,
    };
};
